// DEPENDENCIES
import * as Sentry from "@sentry/react";

// TYPES
import {
  Option,
  PitbullProvinces,
  ShareholderData,
  Step,
  StepFourPJ,
} from "../../types";

// UTILS
import { clearAddressOf, input, pepFields, spouseFields } from "../../inputs";
import {
  mediumSelectedAddressRequired,
  multipleSelectRequired,
  radioOrSingleSelectRequired,
  shareholderCuitPFRequired,
  shareholderCuitPJRequired,
  shareholderCuitSpouse,
  shareholderNifRequired,
} from "../../validations";
import {
  buildFullAddress,
  fuseProvince,
  getDraftStepsToSend,
} from "../../app/utils";
import { sendStep } from "../../connectors/connectors";
import {
  asHighlighted,
  isCompanyWithoutShareholders,
  isEmptyShareholder,
  isUncompletedShareholder,
} from "../../utils";
import {
  emptyJuridicalShareholder,
  emptyPhysicalShareholder,
} from "../../constants";
import {
  addSendedStep,
  onUpdateFormData,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateActiveIndex,
  onUpdateUtil,
} from "../../reducers/collectionReducer";
import { openModal } from "../../reducers/modalReducer";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Icon } from "@allaria-tech/matter/Icon";

// COMPONENTS
import { getCompanyData } from "../../utils/companies";
import Shareholders from "../../components/Shareholders/Shareholders";

const ifPhysical = { name: "personType", value: "PHYSICAL" };
const ifJuridical = { name: "personType", value: "JURIDICAL" };
const ifForeignTaxResidence = { name: "foreignTaxDetailBool", value: "SI" };

const shareholdersStep = (
  countriesOptions: Option[],
  activitiesOptionsPJ: Option[],
  citizenshipOptions: Option[],
  activitiesOptionsPF: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): Omit<Step, "step"> => {
  const foreignTaxCountryOptions = countriesOptions.filter(
    (country) => country.value !== "Argentina",
  );
  const provincesOption = provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));

  return {
    name: "Accionistas",
    subtitle:
      "Cargá accionistas con más del 10%. Si todos tienen menos, ingresá al menos uno. Luego te pediremos la Composición Accionaria.",
    skipWhen: (values) =>
      isCompanyWithoutShareholders(
        values?.businessInformation?.corporateType ?? "",
      ),
    component: (_, __, formik) => <Shareholders formik={formik} />,
    insideForm: true,
    secondaryForm: {
      collectionName: "shareholders",
      inline: true,
      title: (activeIndex) => `Accionista ${activeIndex}`,
      mobileTitle: "Cargar accionista",
      mobileEditTitle: "Editar accionista",
      mobileSections: (activeTab) =>
        activeTab !== "PHYSICAL"
          ? undefined
          : [
              {
                title: "Datos personales",
                fields: [
                  "name",
                  "lastName",
                  "citizenship",
                  "birthDate",
                  "taxType",
                  "taxId",
                  "ownership",
                  "civilStatus",
                  "spouseName",
                  "spouseLastName",
                  "spouseCuil",
                ],
              },
              {
                title: "Dirección",
                fields: [
                  "address.country",
                  "address.fullAddress",
                  "address.floor",
                  "address.apartment",
                  "address.zipCode",
                  "address.municipality",
                  "address.state",
                ],
              },
              {
                title: "Información fiscal y laboral",
                fields: [
                  "activities",
                  "foreignTaxDetailBool",
                  "foreignTaxDetail.address.country",
                  "foreignTaxDetail.address.fullAddress",
                  "foreignTaxDetail.address.floor",
                  "foreignTaxDetail.address.apartment",
                  "foreignTaxDetail.address.zipCode",
                  "foreignTaxDetail.address.municipality",
                  "foreignTaxDetail.address.state",
                  "foreignTaxDetail.idNumber",
                  "pep",
                  "pepDetails",
                ],
              },
            ],
      tabs: {
        options: [
          {
            id: "PHYSICAL",
            label: "Persona física",
            leadingIcon: <Icon name="user-2" size="sm" />,
          },
          {
            id: "JURIDICAL",
            label: "Persona jurídica",
            leadingIcon: <Icon name="company" size="sm" />,
          },
        ],
        active: (activeShareholder: ShareholderData) =>
          activeShareholder?.personType,
        onChange: (tab, setActiveTab, dispatch, state, values) => {
          if (!state || !dispatch || !values)
            throw new Error("Error: state or dispatch not found");

          const {
            onboardingData: { formValues },
            collectionData: { activeIndex },
          } = state;

          if (!activeIndex) throw new Error("Error: activeIndex not found");

          const shareholders = formValues?.shareholders || [];

          const hasSavedData =
            shareholders[activeIndex - 1]?.ownership ||
            values.legalName ||
            values.name;

          // Show alert if user tries to change the tab with saved data
          if (hasSavedData) {
            dispatch(openModal({ name: "save-shareholder" }));
          } else {
            const newShareholders = [...shareholders];

            newShareholders[activeIndex - 1] =
              tab === "PHYSICAL"
                ? emptyPhysicalShareholder
                : emptyJuridicalShareholder;

            dispatch(onUpdateFormData({ shareholders: newShareholders }));
            setActiveTab(tab);
          }
        },
        disabled: (state) => {
          if (!state) throw new Error("Error: state or dispatch not found");

          const {
            onboardingData: { formValues },
            collectionData: { activeIndex },
          } = state;

          if (!activeIndex) {
            new Error("Error: activeIndex not found");
            return true;
          }

          const shareholders = formValues?.shareholders || [];
          const activeShareholder = shareholders[activeIndex - 1];
          const isCompletedShareholder =
            activeShareholder && activeShareholder.ownership;

          return isCompletedShareholder;
        },
      },
      alert: {
        id: "save-shareholder",
        title: "¿Descartar accionista?",
        message:
          "Esta acción no se puede deshacer. Para sumarlo de nuevo al listado, vas a tener que completar sus datos otra vez.",
        primaryButton: {
          text: "Sí, eliminar",
          action: (tab, dispatch, state) => {
            if (!state || !dispatch)
              throw new Error("Error: state or dispatch not found");

            const {
              onboardingData: { formValues },
              collectionData: { activeIndex },
            } = state;

            if (!activeIndex) throw new Error("Error: activeIndex not found");

            const shareholders = formValues?.shareholders || [];

            const newShareholders = [...shareholders];

            newShareholders[activeIndex - 1] =
              tab === "PHYSICAL"
                ? emptyJuridicalShareholder
                : emptyPhysicalShareholder;

            dispatch(onUpdateFormData({ shareholders: newShareholders }));
          },
        },
      },
      deleteEntity: {
        alert: {
          title: ({ personType, name, lastName, legalName }) => {
            const shareholderName =
              personType === "JURIDICAL"
                ? legalName
                : [name, lastName].filter(Boolean).join(" ");

            return shareholderName ? (
              <>¿Eliminar a {asHighlighted(shareholderName)} como accionista?</>
            ) : (
              "¿Eliminar accionista?"
            );
          },
          message:
            "Esta acción no se puede deshacer. Para sumarlo de nuevo al listado, vas a tener que completar sus datos otra vez.",
        },
        // The first shareholder starts as an empty form, so until something is
        // stored there is nothing to remove
        disabled: (state) => {
          const shareholders: ShareholderData[] =
            state.onboardingData.formValues?.shareholders || [];

          return (
            shareholders.length === 1 &&
            isUncompletedShareholder(shareholders[0])
          );
        },
        onClick: (dispatch, state, setPrimaryFormValues) => {
          const { activeIndex } = state.collectionData;
          if (!activeIndex) return;

          const shareholders: ShareholderData[] =
            state.onboardingData.formValues?.shareholders || [];
          const removedShareholder = shareholders[activeIndex - 1];
          const newShareholders = shareholders.filter(
            (_, i) => i !== activeIndex - 1,
          );

          dispatch(onUpdateFormData({ shareholders: newShareholders }));

          const sentStepFour = state.onboardingData.sendedSteps?.find(
            (step): step is StepFourPJ => step["@type"] === "4_JURIDICAL",
          );
          if (sentStepFour && removedShareholder?.taxId) {
            const stepFour: StepFourPJ = {
              ...sentStepFour,
              shareholders: sentStepFour.shareholders.filter(
                ({ taxId }) => taxId !== removedShareholder.taxId,
              ),
            };

            dispatch(addSendedStep(stepFour));

            sendStep({
              stepId: 4,
              body: {
                companyType: getCompanyData().companyName,
                personType: "JURIDICAL",
                email: state.onboardingData.formValues?.email || "",
                steps: getDraftStepsToSend(
                  state.onboardingData.sendedSteps,
                  stepFour,
                ),
              },
            }).catch((error) => Sentry.captureException(error));
          }
          // Removing the shareholder being edited falls back to the last one
          // loaded, the same one the step opens on
          dispatch(onUpdateActiveIndex(Math.max(newShareholders.length, 1)));

          setPrimaryFormValues?.((prevValues) => ({
            ...prevValues,
            shareholders: newShareholders,
          }));
        },
      },
      showErrorsOnOpen: (shareholder) =>
        !isEmptyShareholder(shareholder as ShareholderData) &&
        isUncompletedShareholder(shareholder as ShareholderData),
      fields: [
        input("legalName", { conditional: ifJuridical }),
        input("name", { conditional: ifPhysical }),
        input("lastName", { conditional: ifPhysical }),
        input("citizenship", {
          options: citizenshipOptions,
          validation: radioOrSingleSelectRequired(citizenshipOptions),
          conditional: ifPhysical,
          disabled: false,
        }),
        input("birthDate", { conditional: ifPhysical, disabled: false }),
        input("taxType", {
          onChange: (
            _values,
            setFieldValue,
            __,
            ___,
            value,
            state,
            dispatch,
          ) => {
            setFieldValue("taxId", "");

            if (!state || !dispatch)
              throw new Error("Error: state or dispatch not found");

            // We set 'util' with the value of taxType so it can be read from the conditionalValidation of taxId
            if (value && typeof value === "string") {
              dispatch(onUpdateUtil(value));
            } else if (value && value.target) {
              dispatch(onUpdateUtil(value.target.value));
            }
          },
        }),
        input("taxId", {
          conditionalValidation: (state) => {
            if (!state) throw new Error("Error: state not found");
            const activeIndex = state.collectionData.activeIndex || 1;
            const taxTypeSelected = state.collectionData.util;

            const { formValues } = state.onboardingData;
            const shareholders: ShareholderData[] =
              formValues?.shareholders || [];

            const activePersonType = shareholders[activeIndex - 1]?.personType;

            const shareholdersCuit = shareholders.map(
              (shareholder) => shareholder?.taxId || "",
            );

            const cuitsExcludingActiveIndex = shareholdersCuit.filter(
              (_, index) => activeIndex !== null && index !== activeIndex - 1,
            );

            return taxTypeSelected === "CUIT"
              ? activePersonType === "PHYSICAL"
                ? shareholderCuitPFRequired(cuitsExcludingActiveIndex)
                : shareholderCuitPJRequired([
                    ...cuitsExcludingActiveIndex,
                    state?.onboardingData?.formValues?.businessInformation.cuit,
                  ])
              : shareholderNifRequired(cuitsExcludingActiveIndex);
          },
        }),
        input("ownership"),
        input("civilStatus", {
          conditional: ifPhysical,
        }),
        ...spouseFields({
          spouseCuil: { validation: shareholderCuitSpouse },
        }),
        input("address.country", {
          options: countriesOptions,
          validation: radioOrSingleSelectRequired(countriesOptions),
          onChange: clearAddressOf("address"),
          conditional: ifPhysical,
        }),
        input("address.fullAddress", {
          autoSelect: "address.fullAddress",
          country: "address.country",
          validation: mediumSelectedAddressRequired,
          conditional: ifPhysical,
          onChange: (values, setFieldValue) => {
            const {
              formattedAddress: {
                postalCode: zipCode,
                state,
                city,
                municipality,
                street,
                streetNumber: number,
                country,
              },
            } = values;

            const provinces = getProvincesFilteredByCountry(country);

            const fusedProvince = fuseProvince(provinces, state, city);

            //// If no records are found in FUSE, we set the first province by default to avoid breaking
            const finalProvince = fusedProvince || provinces[0].name;

            if (zipCode) setFieldValue("address.zipCode", zipCode);
            setFieldValue("address.state", finalProvince);
            setFieldValue("address.city", city);
            setFieldValue("address.municipality", municipality);
            setFieldValue("address.street", street);
            setFieldValue("address.number", number);

            const newFullAddress = buildFullAddress(
              street,
              number,
              city,
              municipality,
            );

            setFieldValue("address.fullAddress", newFullAddress);
          },
        }),
        input("address.floor", { conditional: ifPhysical }),
        input("address.apartment", { conditional: ifPhysical }),
        input("address.zipCode", { conditional: ifPhysical }),
        input("address.municipality", { conditional: ifPhysical }),
        input("address.state", {
          validation: radioOrSingleSelectRequired(provincesOption),
          conditional: ifPhysical,
          dynamicOptions: (formik) => {
            if (!formik)
              Sentry.captureMessage(
                "No existe formik en el dynamicOptions de provincias",
                {
                  level: "info",
                },
              );
            const country = formik.values.address?.country || "Argentina";

            //Filter provinces by country
            const provinces = getProvincesFilteredByCountry(country);
            if (!provinces || provinces.length === 0) {
              Sentry.captureMessage(
                "El formik value de country es: " +
                  formik.values.address?.country +
                  " y el country es " +
                  country,
              );
              Sentry.captureMessage(
                `Provincias filtradas por país: ${JSON.stringify(
                  provinces,
                )} y el country es ${country}`,
                {
                  level: "info",
                },
              );
            }

            const provincesOption = provinces.map((prov) => ({
              value: prov.name,
              label: prov.name,
            }));

            return provincesOption;
          },
        }),
        input("activities", {
          conditionalValidation(state) {
            if (!state) throw new Error("Error: state not found");
            const activeIndex = state.collectionData?.activeIndex || 0;

            const { formValues } = state.onboardingData;
            const shareholders: ShareholderData[] =
              formValues?.shareholders || [];

            const shareholderPersonType =
              shareholders[activeIndex - 1]?.personType;

            if (shareholderPersonType === "PHYSICAL") {
              return multipleSelectRequired(activitiesOptionsPF);
            } else {
              return multipleSelectRequired(activitiesOptionsPJ);
            }
          },
          dynamicOptions: (_formik, state) => {
            if (!state) throw new Error("Error: state not found");
            const activeIndex = state.collectionData?.activeIndex || 0;

            const { formValues } = state.onboardingData;
            const shareholders: ShareholderData[] =
              formValues?.shareholders || [];

            const shareholderPersonType =
              shareholders[activeIndex - 1]?.personType;

            if (shareholderPersonType === "PHYSICAL") {
              return activitiesOptionsPF;
            } else {
              return activitiesOptionsPJ;
            }
          },
        }),
        input("foreignTaxDetailBool"),
        input("address.country", {
          name: "foreignTaxDetail.address.country",
          label: "País de residencia fiscal",
          options: foreignTaxCountryOptions,
          validation: radioOrSingleSelectRequired(foreignTaxCountryOptions),
          onChange: clearAddressOf("foreignTaxDetail.address"),
          preconfirmationLabel: "Residencia fiscal",
          conditional: ifForeignTaxResidence,
        }),
        input("address.fullAddress", {
          name: "foreignTaxDetail.address.fullAddress",
          autoSelect: "foreignTaxDetail.address.fullAddress",
          country: "foreignTaxDetail.address.country",
          validation: mediumSelectedAddressRequired,
          conditional: ifForeignTaxResidence,
          formatInPreConfirmation: (value) =>
            value ? value?.split(",")?.[0] : "",
          onChange: (values, setFieldValue) => {
            const {
              formattedAddress: {
                postalCode: zipCode,
                state,
                city,
                municipality,
                street,
                streetNumber: number,
                country,
              },
            } = values;

            const provinces = getProvincesFilteredByCountry(country);

            const fusedProvince = fuseProvince(provinces, state, city);

            //// If no records are found in FUSE, we set the first province by default to avoid breaking
            const finalProvince = fusedProvince || provinces[0].name;

            setFieldValue("foreignTaxDetail.address.zipCode", zipCode);
            setFieldValue("foreignTaxDetail.address.state", finalProvince);
            setFieldValue("foreignTaxDetail.address.city", city);
            setFieldValue(
              "foreignTaxDetail.address.municipality",
              municipality,
            );
            setFieldValue("foreignTaxDetail.address.street", street);
            setFieldValue("foreignTaxDetail.address.number", number);

            const newFullAddress = buildFullAddress(
              street,
              number,
              city,
              municipality,
            );

            setFieldValue(
              "foreignTaxDetail.address.fullAddress",
              newFullAddress,
            );
          },
        }),
        input("address.floor", {
          name: "foreignTaxDetail.address.floor",
          conditional: ifForeignTaxResidence,
        }),
        input("address.apartment", {
          name: "foreignTaxDetail.address.apartment",
          conditional: ifForeignTaxResidence,
        }),
        input("address.zipCode", {
          name: "foreignTaxDetail.address.zipCode",
          conditional: ifForeignTaxResidence,
        }),
        input("address.municipality", {
          name: "foreignTaxDetail.address.municipality",
          conditional: ifForeignTaxResidence,
        }),
        input("address.state", {
          name: "foreignTaxDetail.address.state",
          validation: radioOrSingleSelectRequired(provincesOption),
          conditional: ifForeignTaxResidence,
          dynamicOptions: (formik) => {
            const country = formik.values.foreignTaxDetail?.address.country;
            const provinces = getProvincesFilteredByCountry(country);
            const provincesOption = provinces.map((prov) => ({
              value: prov.name,
              label: prov.name,
            }));
            return provincesOption;
          },
        }),
        input("foreignTaxDetail.idNumber"),
        ...pepFields({ pep: { conditional: ifPhysical } }),
      ],
      onSave: (values, dispatch, state, setPrimaryFormValues) => {
        if (!state || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const {
          onboardingData: { formValues },
          collectionData: { activeIndex },
        } = state;

        if (!activeIndex) throw new Error("Error: activeIndex not found");

        const shareholders = formValues?.shareholders || [];

        const otherShareholders = shareholders.filter(
          (shareholder: ShareholderData) => shareholder?.taxId !== values.taxId,
        );
        const totalOwnership = otherShareholders.reduce(
          (acc: number, shareholder: ShareholderData) =>
            acc + (Number(shareholder?.ownership) || 0),
          0,
        );
        // The field hands over its percentage as text
        if (Number(values.ownership) + totalOwnership > 100) {
          showToastMessage("La suma de accionistas no debe superar el 100%.", {
            containerId: "formAlert",
            type: "ERROR",
          });
        } else {
          const newShareholders = [...shareholders];
          newShareholders[activeIndex - 1] = values;
          setPrimaryFormValues &&
            setPrimaryFormValues((prevValues) => ({
              ...prevValues,
              shareholders: newShareholders,
            }));

          dispatch(onUpdateFormData({ shareholders: newShareholders }));
          showToastMessage("El accionista fue guardado exitosamente.", {
            containerId: "layoutPage",
            type: "SUCCESS",
          });
        }
      },
    },
  };
};

export default shareholdersStep;
